import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
const _excluded = ["reportNumber", "reportTypeName"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { createSelector } from 'reselect';
import PropTypes from 'prop-types';
import { get as getValueAtPath } from 'lodash';
import { mergeDeepLeft } from 'ramda';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidV4 } from 'uuid';
import moment from 'moment';
import { getValue, appendPath, setValue, pathComparator, enumerateWildcardPath } from '@truqc/content-path';
import { generateMergePatch } from '../../../../behaviors/src/lib/mergePatch';
import entityComposition from '../../common/classes/entityComposition';
import PopoverButton from '../../common/controls/popoverButton';
import Label from './label';
import FillFromPreviousButtonHeader from './fillFromPreviousButtonHeader';
import { getContent, getFormat, getCurrentJobID } from '../selectors/simpleSelectors';
import { newPathSelector } from '../selectors';
import logValueOnChange from '../commonActions/logValueOnChange';
const safeGetValue = (path, content) => {
  if (path === '/') {
    return content;
  }
  return getValue(path, content);
};
const sourcesSelector = createSelector([getFormat], format => format && format.sources);
const activeContentAtPathSelector = createSelector([getContent, newPathSelector], (content, path) => safeGetValue(path, content));
export const fetchReportTypes = (reportTypeActions, sharedReportTypes) => {
  reportTypeActions.serverIndex(sharedReportTypes.indexQuery);
};
export const fillContentEffect = (addToCollection, activeContentAtPath, duplicateContentPaths, fillTarget, onChange, onToggle, path, reportsLoading, reportShowCancel, selectedSource, selected, sources) => {
  if (selected && !reportsLoading && Object.keys(fillTarget).length > 0) {
    // prepare the selected report's data for copying to current report
    // copy over the selected report data
    let fillContent = getValueAtPath(fillTarget, 'content', null);
    // Need to grab and account for static content in the target report
    const fillStaticContent = getValueAtPath(fillTarget, 'ReportType.staticContent', null);
    if (fillStaticContent) {
      fillContent = mergeDeepLeft(fillContent, JSON.parse(fillStaticContent));
    }
    const fillID = getValueAtPath(fillTarget, 'id', null);
    if (fillContent && selected === fillID) {
      let value = safeGetValue(path, fillContent);
      const previousValue = path === '/' ? {} : activeContentAtPath;
      // derive new value for filling specially if format has "sources"
      const selectedSourceID = getValueAtPath(selectedSource, 'id', null);
      if (addToCollection) {
        // value is an object with UUIDs for keys. keep the values but generate new UUID keys
        const values = Object.values(value);
        value = {};
        const now = moment();
        values.sort(pathComparator('_created')).forEach((val, index) => {
          let newValue = {};
          if (duplicateContentPaths.length > 0) {
            duplicateContentPaths.forEach(dupPath => {
              enumerateWildcardPath(val, dupPath, (cp, c) => {
                newValue = setValue(cp, newValue, c);
              });
            });
          } else {
            newValue = val;
          }
          // eslint-disable-next-line no-underscore-dangle
          newValue._created = now.add(index * 10, 'ms').utc().toISOString(); // set a consistent created date
          value[uuidV4().toUpperCase()] = newValue;
        });
      } else if (selectedSourceID && sources) {
        value = {};
        // match selectedSource to sources defined in report.format
        const source = sources.find(s => s.identifier === selectedSourceID);
        const mapKeys = Object.keys(source.map);
        mapKeys.forEach(pathTarget => {
          const pathFill = source.map[pathTarget];
          value = setValue(pathTarget, value, getValue(pathFill, fillContent));
        });
      }
      let changeValue = _objectSpread(_objectSpread({}, previousValue || {}), value || {});
      // Extra bit of logic to stop us from accidently overwriting content at '/'
      let changePath = path;
      if (changePath === '/') {
        const _Object$keys = Object.keys(changeValue),
          _Object$keys2 = _slicedToArray(_Object$keys, 1),
          key = _Object$keys2[0];
        changePath = appendPath(changePath, key);
        changeValue = changeValue[key];
      }

      // Generate a diff to the previous value in content to ensure everything is properly nulled
      const mergePatch = generateMergePatch(getValue(changePath, activeContentAtPath), changeValue) || {};
      onChange({
        path: changePath,
        value: mergeDeepLeft(changeValue, mergePatch)
      });
      reportShowCancel();
    }

    // cleanup
    onToggle();
  }
};
const FillFromPreviousButton = _ref => {
  let _ref$activeContentAtP = _ref.activeContentAtPath,
    activeContentAtPath = _ref$activeContentAtP === void 0 ? {} : _ref$activeContentAtP,
    activeID = _ref.activeID,
    _ref$addToCollection = _ref.addToCollection,
    addToCollection = _ref$addToCollection === void 0 ? false : _ref$addToCollection,
    _ref$currentJobID = _ref.currentJobID,
    currentJobID = _ref$currentJobID === void 0 ? null : _ref$currentJobID,
    _ref$duplicateContent = _ref.duplicateContentPaths,
    duplicateContentPaths = _ref$duplicateContent === void 0 ? [] : _ref$duplicateContent,
    _ref$enabled = _ref.enabled,
    enabled = _ref$enabled === void 0 ? true : _ref$enabled,
    _ref$fillReportSelect = _ref.fillReportSelectEditorActions,
    indexCancelReport = _ref$fillReportSelect.indexCancel,
    serverFilterReports = _ref$fillReportSelect.serverFilter,
    serverShowReport = _ref$fillReportSelect.serverShow,
    reportShowCancel = _ref$fillReportSelect.showCancel,
    _ref$fillReportSelect2 = _ref.fillReportSelectJobActions,
    indexCancelJob = _ref$fillReportSelect2.indexCancel,
    serverFilterJobs = _ref$fillReportSelect2.serverFilter,
    _ref$fillTarget = _ref.fillTarget,
    fillTarget = _ref$fillTarget === void 0 ? {} : _ref$fillTarget,
    _ref$filterByCurrentJ = _ref.filterByCurrentJob,
    filterByCurrentJob = _ref$filterByCurrentJ === void 0 ? false : _ref$filterByCurrentJ,
    jobIndexQuery = _ref.jobIndexQuery,
    jobsLoading = _ref.jobsLoading,
    jobs = _ref.jobs,
    _ref$label = _ref.label,
    label = _ref$label === void 0 ? null : _ref$label,
    onChange = _ref.onChange,
    path = _ref.path,
    reportsLoading = _ref.reportsLoading,
    reportIndexQuery = _ref.reportIndexQuery,
    reports = _ref.reports,
    sharedReportTypes = _ref.reportType,
    reportTypeActions = _ref.reportTypeActions,
    _ref$reportTypeFillFr = _ref.reportTypeFillFromActions,
    indexCancelReportType = _ref$reportTypeFillFr.indexCancel,
    serverFilterReportTypes = _ref$reportTypeFillFr.serverFilter,
    reportTypeID = _ref.reportTypeID,
    reportTypeIndexQuery = _ref.reportTypeIndexQuery,
    reportTypes = _ref.reportTypes,
    reportTypesLoading = _ref.reportTypesLoading,
    _ref$sources = _ref.sources,
    sources = _ref$sources === void 0 ? null : _ref$sources,
    _ref$testingIdentifie = _ref.testingIdentifier,
    testingIdentifier = _ref$testingIdentifie === void 0 ? '' : _ref$testingIdentifie,
    title = _ref.title;
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isOpen = _useState2[0],
    setIsOpen = _useState2[1];
  const _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    selected = _useState4[0],
    setSelected = _useState4[1];
  const _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    selectedJob = _useState6[0],
    setSelectedJob = _useState6[1];
  const _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    selectedSource = _useState8[0],
    setSelectedSource = _useState8[1];
  useEffect(() => {
    fetchReportTypes(reportTypeActions, sharedReportTypes);
  }, [reportTypeActions, sharedReportTypes]);

  // the list will be jobs, reportTypes, or reports
  const list = useMemo(() => {
    let result = [];
    if (!selectedJob) {
      // jobs
      if (!jobsLoading) {
        result = jobs.map(job => ({
          id: job.id,
          isJob: true,
          name: job.jobNumber
        }));
      }
    } else if (sources && !selectedSource) {
      // reportTypes
      if (!reportTypesLoading) {
        reportTypes.forEach(rt => {
          const source = sources.find(s => s.identifier === rt.identifier);
          if (source) {
            const pathFill = [];
            const mapKeys = Object.keys(source.map);
            mapKeys.forEach(pathTarget => {
              pathFill.push(source.map[pathTarget]);
            });
            result.push({
              id: rt.identifier,
              isSource: true,
              name: source.title,
              showItemIfValue: source.showItemIfValue,
              pathFill // where the content is coming from
            });
          }
        });
      }
    } else {
      // reports
      result = reports.map(_ref2 => {
        let reportNumber = _ref2.reportNumber,
          reportTypeName = _ref2.reportTypeName,
          rest = _objectWithoutProperties(_ref2, _excluded);
        return _objectSpread(_objectSpread({}, rest), {}, {
          name: "".concat(reportTypeName, " ").concat(reportNumber)
        });
      });
    }
    return result;
  }, [jobs, jobsLoading, reports, reportTypes, reportTypesLoading, selectedJob, selectedSource, sources]);
  const paths = useMemo(() => {
    const result = [];
    if (sources) {
      sources.forEach(source => {
        const mapKeys = Object.keys(source.map);
        mapKeys.forEach(pathTarget => {
          result.push(source.map[pathTarget]);
        });
      });
    } else {
      result.push(path);
    }
    return result;
  }, [path, sources]);
  const onSelect = useCallback(item => {
    const id = item.id,
      isJob = item.isJob,
      isSource = item.isSource,
      _item$pathFill = item.pathFill,
      pathFill = _item$pathFill === void 0 ? [] : _item$pathFill,
      showItemIfValue = item.showItemIfValue;
    const reportContentPaths = paths.concat(pathFill);
    if (isJob) {
      setSelectedJob(id);
      if (!sources) {
        // fetch reports
        const onlyOnDemand = false; // always re-fetch for up-to-date lists
        serverFilterReports({
          contentPath: reportContentPaths,
          reportType: [reportTypeID],
          // only filter on the same reportType
          showItemIfValue,
          job: [id]
        }, reportIndexQuery, onlyOnDemand);
      } else {
        // fetch reportTypes
        const onlyOnDemand = false; // always re-fetch for up-to-date lists
        serverFilterReportTypes({
          reportContentPath: paths,
          job: [id]
        }, reportTypeIndexQuery, onlyOnDemand);
      }
    } else if (isSource) {
      const onlyOnDemand = false; // always re-fetch for up-to-date lists
      serverFilterReports({
        contentPath: reportContentPaths,
        showItemIfValue,
        job: [selectedJob]
      }, reportIndexQuery, onlyOnDemand);
      setSelectedSource(item);
    } else {
      serverShowReport(id);
      setSelected(id);
    }
  }, [paths, selectedJob, serverFilterReports, serverFilterReportTypes, serverShowReport, reportIndexQuery, reportTypeID, reportTypeIndexQuery, sources]);
  const onToggle = useCallback(() => {
    if (!isOpen) {
      const reportTypeIDs = [];
      if (sources) {
        const sourceReportTypes = sources.map(source => source.identifier);
        // filter list for relevant reportTypeIDs and fetch jobs
        sharedReportTypes.list.filter(rt => sourceReportTypes.includes(rt.identifier)).forEach(rt => reportTypeIDs.push(rt.id));
      } else {
        // if no format.sources are defined, filter with the current report's reportType
        reportTypeIDs.push(reportTypeID);
      }
      const filters = {
        closed: false,
        reportsToExclude: [activeID],
        reportContentPath: paths,
        reportType: reportTypeIDs
      };
      if (!filterByCurrentJob) {
        const onlyOnDemand = false; // always fetch new list of jobs
        serverFilterJobs(filters, jobIndexQuery, onlyOnDemand);
      } else {
        // Don't try to query the server, we already have the current
        // job ID
        onSelect({
          id: currentJobID,
          isJob: true
        });
      }
    } else {
      // clean up state
      indexCancelJob();
      indexCancelReportType();
      indexCancelReport();
      setSelectedJob(null);
      setSelectedSource(null);
      setSelected(null);
    }
    setIsOpen(!isOpen); // controls UI state
  }, [activeID, sources, indexCancelJob, indexCancelReport, indexCancelReportType, isOpen, jobIndexQuery, paths, reportTypeID, serverFilterJobs, sharedReportTypes.list, filterByCurrentJob, onSelect, currentJobID]);

  // handle a selected report
  useEffect(() => {
    fillContentEffect(addToCollection, activeContentAtPath, duplicateContentPaths, fillTarget, onChange, onToggle, path, reportsLoading, reportShowCancel, selectedSource, selected, sources);
  }, [addToCollection, activeContentAtPath, duplicateContentPaths, fillTarget, onChange, onToggle, path, reportsLoading, reportShowCancel, selectedSource, selected, sources]);
  const loaded = !jobsLoading && !reportTypesLoading && !reportsLoading;
  return /*#__PURE__*/React.createElement("div", null, label !== null ? /*#__PURE__*/React.createElement(Label, {
    value: label
  }) : undefined, /*#__PURE__*/React.createElement(PopoverButton, {
    enabled: enabled,
    header: /*#__PURE__*/React.createElement(FillFromPreviousButtonHeader, {
      jobIndexQuery: jobIndexQuery,
      jobServerFilter: serverFilterJobs,
      showBack: !filterByCurrentJob,
      selectedJob: selectedJob,
      selectedSource: selectedSource,
      setSelectedJob: setSelectedJob,
      setSelectedSource: setSelectedSource,
      sources: sources
    }),
    isOpen: isOpen,
    list: list,
    onSelect: onSelect,
    onToggle: onToggle,
    title: title,
    testingIdentifier: testingIdentifier,
    className: "btn-block tqc-control",
    loaded: loaded
  }));
};
export { FillFromPreviousButton as UncomposedFillFromPreviousButton };
FillFromPreviousButton.propTypes = {
  activeContentAtPath: PropTypes.shape({}),
  activeID: PropTypes.string.isRequired,
  addToCollection: PropTypes.bool,
  duplicateContentPaths: PropTypes.arrayOf(PropTypes.string),
  enabled: PropTypes.bool,
  currentJobID: PropTypes.string,
  filterByCurrentJob: PropTypes.bool,
  fillReportSelectEditorActions: PropTypes.shape({
    indexCancel: PropTypes.func,
    serverFilter: PropTypes.func,
    serverShow: PropTypes.func,
    showCancel: PropTypes.func
  }).isRequired,
  fillReportSelectJobActions: PropTypes.shape({
    indexCancel: PropTypes.func,
    serverFilter: PropTypes.func
  }).isRequired,
  fillTarget: PropTypes.shape({
    id: PropTypes.string
  }),
  jobIndexQuery: PropTypes.shape({}).isRequired,
  jobsLoading: PropTypes.bool.isRequired,
  jobs: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  label: PropTypes.string,
  reportsLoading: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  path: PropTypes.string.isRequired,
  reportIndexQuery: PropTypes.shape({}).isRequired,
  reports: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  reportTypeActions: PropTypes.shape({
    serverIndex: PropTypes.func
  }).isRequired,
  reportType: PropTypes.shape({
    indexQuery: PropTypes.shape({}),
    list: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  reportTypeFillFromActions: PropTypes.shape({
    indexCancel: PropTypes.func,
    serverFilter: PropTypes.func
  }).isRequired,
  reportTypeID: PropTypes.string.isRequired,
  reportTypeIndexQuery: PropTypes.shape({}).isRequired,
  reportTypes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  reportTypesLoading: PropTypes.bool.isRequired,
  sources: PropTypes.arrayOf(PropTypes.shape({})),
  testingIdentifier: PropTypes.string,
  title: PropTypes.string.isRequired
};
export default entityComposition(FillFromPreviousButton, ['reportType'], {
  currentJobID: getCurrentJobID,
  activeContentAtPath: activeContentAtPathSelector,
  activeID: state => state.report.active.id,
  fillTarget: state => state.fillReportSelectEditor.active,
  jobIndexQuery: state => state.fillReportSelectJob.indexQuery,
  jobsLoading: state => state.fillReportSelectJob.loading,
  jobs: state => state.fillReportSelectJob.list,
  reportsLoading: state => state.fillReportSelectEditor.loading,
  reportIndexQuery: state => state.fillReportSelectEditor.indexQuery,
  // Kick the current report out of the potential list of reports
  reports: state => state.fillReportSelectEditor.list.filter(_ref3 => {
    let id = _ref3.id;
    return id !== state.report.active.id;
  }),
  reportTypeID: state => state.report.active.reportTypeID,
  reportTypeIndexQuery: state => state.reportTypeFillFrom.indexQuery,
  reportTypes: state => state.reportTypeFillFrom.list,
  reportTypesLoading: state => state.reportTypeFillFrom.loading,
  sources: sourcesSelector,
  path: newPathSelector,
  onChange: logValueOnChange
}, ['fillReportSelectEditor', 'fillReportSelectJob', 'reportTypeFillFrom']);